import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import HiredLogo from "@/components/brand/HiredLogo";
import { Loader2, CheckCircle2, XCircle, ArrowRight } from "lucide-react";

export default function VerifyMagicLink() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // verifying | ok | error
  const [message, setMessage] = useState("");
  const nav = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setStatus("error"); setMessage("Missing verification token."); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-magic-link", { token });
        localStorage.setItem("hired_token", data.token);
        localStorage.setItem("hired_token_kind", "user");
        setUser(data.user);
        sessionStorage.removeItem("hired_pending_email");
        sessionStorage.removeItem("hired_pending_employee");
        sessionStorage.removeItem("hired_dev_link");
        setStatus("ok");
        // small delay so user sees the confirmation
        setTimeout(() => {
          if (data.user.onboarding_complete) nav("/dashboard");
          else nav("/onboarding");
        }, 900);
      } catch (err) {
        setStatus("error");
        setMessage(err?.response?.data?.detail || "This link is invalid or expired.");
      }
    })();
  }, [params, nav, setUser]);

  return (
    <div className="min-h-screen bg-obsidian text-ivory flex flex-col">
      <header className="border-b border-line px-6 md:px-10 py-5"><Link to="/"><HiredLogo size={22} /></Link></header>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-lg text-center">
          {status === "verifying" && (<>
            <Loader2 className="text-gold animate-spin mx-auto" size={28} />
            <h1 className="brand-headline text-4xl md:text-5xl mt-8">Activating your <span className="text-purple italic">trial…</span></h1>
            <p className="text-steel mt-4">One moment.</p>
          </>)}
          {status === "ok" && (<>
            <CheckCircle2 className="text-gold mx-auto" size={36} />
            <h1 className="brand-headline text-4xl md:text-5xl mt-8">Trial <span className="text-purple italic">active.</span></h1>
            <p className="text-steel mt-4">Redirecting you to your workspace…</p>
          </>)}
          {status === "error" && (<>
            <XCircle className="text-red-400 mx-auto" size={36} />
            <h1 className="brand-headline text-4xl md:text-5xl mt-8">Link expired</h1>
            <p className="text-steel mt-4">{message}</p>
            <Link to="/signup" data-testid="retry-signup" className="btn-gold mt-8 inline-flex">Request a new link <ArrowRight size={16} /></Link>
          </>)}
        </div>
      </div>
    </div>
  );
}
